import * as React from 'react';
import { IItemCache } from '../../../models/IItemCacheObserver';
import {
  isNewFacet,
  isCheckedOutFacet,
  malwareDetectedFacet,
  missingMetadataFacet,
  policyTipFacet,
  displayNameFacet,
  showRetentionPolicyIconFacet,
  followedItemFacet,
  isFollowedFacet,
  analyticsItemFacet
} from '../../../facets/ItemFacets';
import { isTrendingFacet } from '../../../facets/ItemAnalyticsFacets';
import {
  configurationItemKey,
  enableTrendingConfigurationFacet
} from '../../../facets/ConfigurationFacets';
import {
  NewSignal,
  MalwareDetectedSignal,
  YouCheckedOutSignal,
  SomeoneCheckedOutSignal,
  MissingMetadataSignal,
  BlockedSignal,
  WarningSignal,
  RecordSignal,
  FollowedSignal,
  TrendingSignal
} from '@uifabric/experiments/lib/components/signals/Signals';
import { PolicyTipType } from '../../../models/ItemPolicy';
import * as StringHelper from '@ms/utilities-strings/lib/index';
import strings from '../FieldStrings.resx';
import { ISignalData, itemLifeCycleSignalsControl } from '../../../controls/itemLifeCycleSignalsControl';

const getSignalFuncs = [
  getNewSignal,
  getMalwareSignal,
  getYouCheckedOutSignal,
  getDlpBlockedSignal,
  getMissingMetadataSignal,
  getDlpWarningSignal,
  getAwaitingApprovalSignal,
  getTrendingSignal,
  getSomeoneCheckedOutSignal,
  getRetentionRecordSignal,
  getFollowedSignal
];

export const newSignalKey = 'newSignalKey';
export const youCheckedOutSignalKey = 'youCheckedOutSignalKey';
export const someoneCheckedOutSignalKey = 'someoneCheckedOutSignalKey';
export const malwareSignalKey = 'malwareSignalKey';
export const dlpBlockedSignalKey = 'dlpBlockedSignalKey';
export const missingMetadataSignalKey = 'missingMetadataSignalKey';
export const dlpWarningSignalKey = 'dlpWarningSignalKey';
export const retentionRecordSignalKey = 'retentionRecordSignalKey';
export const followedSignalKey = 'followedSignalKey';
export const trendingSignalKey = 'trendingSignalKey';

export { ISignalData };

export function getStorageSignal(itemCache: IItemCache, itemKey: string): ISignalData | null {
  // To be implemented
  return null;
}

export function getSocialSignal(itemCache: IItemCache, itemKey: string): ISignalData | null {
  // To be implemented
  return null;
}

export function getLifeCycleSignals(
  itemCache: IItemCache,
  itemKey: string,
  maxCount: number,
  includeNew: boolean
): ISignalData[] {
  let signals: ISignalData[] = [];
  let funcIndex = includeNew ? 0 : 1;

  while (signals.length < maxCount && funcIndex < getSignalFuncs.length) {
    let getSignalFunc = getSignalFuncs[funcIndex];
    let signal = getSignalFunc(itemCache, itemKey, String(funcIndex));
    if (signal) {
      signals.push(signal);
    }
    funcIndex++;
  }

  const controlHandler = itemCache.demandItemFacet(itemLifeCycleSignalsControl, itemKey);
  const {
    signals: finalSignals
  } = itemLifeCycleSignalsControl.evaluate(controlHandler)(itemCache, {
    itemKey,
    signals
  });

  return finalSignals;
}

export function getNewSignal(itemCache: IItemCache, itemKey: string, key: string): ISignalData | null {
  const isNew = itemCache.demandItemFacet(isNewFacet, itemKey);
  return isNew ? {
    signal: (
      <NewSignal
        key={ key }
        ariaLabel={ strings.itemSignalNew }
        title={ strings.itemSignalNewTooltip }
      />
    ),
    title: strings.itemSignalNewTooltip,
    key: newSignalKey
  } : null;
}

export function getYouCheckedOutSignal(itemCache: IItemCache, itemKey: string, key: string): ISignalData | null {
  const isCheckedOut = itemCache.demandItemFacet(isCheckedOutFacet, itemKey);
  if (isCheckedOut && isCheckedOut.byYou) {
    return {
      signal: (
        <YouCheckedOutSignal
          key={ key }
          ariaLabel={ strings.itemSignalYouCheckedOut }
          title={ strings.itemSignalYouCheckedOutTooltip }
        />
      ),
      title: strings.itemSignalYouCheckedOutTooltip,
      key: youCheckedOutSignalKey
    };
  }
  return null;
}

export function getSomeoneCheckedOutSignal(itemCache: IItemCache, itemKey: string, key: string): ISignalData | null {
  const isCheckedOut = itemCache.demandItemFacet(isCheckedOutFacet, itemKey);
  if (isCheckedOut && !!isCheckedOut.byOther) {
    const userFacets = itemCache.demandItem({
      displayName: displayNameFacet
    }, isCheckedOut.byOther);

    const titleString = userFacets && userFacets.displayName ?
      StringHelper.format(strings.itemSignalSomeoneCheckedOutTooltip, userFacets.displayName) :
      strings.itemSignalSomeoneCheckedOut;

    return {
      signal: (
        <SomeoneCheckedOutSignal
          key={ key }
          ariaLabel={ strings.itemSignalSomeoneCheckedOut }
          title={ titleString }
        />
      ),
      title: titleString,
      key: someoneCheckedOutSignalKey
    };
  }
  return null;
}

export function getMalwareSignal(itemCache: IItemCache, itemKey: string, key: string): ISignalData | null {
  const isMalwareDetected = itemCache.demandItemFacet(malwareDetectedFacet, itemKey);
  return isMalwareDetected ? {
    signal: (
      <MalwareDetectedSignal
        key={ key }
        ariaLabel={ strings.itemSignalMalwareDetected }
        title={ strings.itemSignalMalwareDetectedTooltip }
      />
    ),
    title: strings.itemSignalMalwareDetectedTooltip,
    key: malwareSignalKey
  } : null;
}

export function getDlpBlockedSignal(itemCache: IItemCache, itemKey: string, key: string): ISignalData | null {
  const policyTip = itemCache.demandItemFacet(policyTipFacet, itemKey);
  if (policyTip &&
    (policyTip === PolicyTipType.block ||
      policyTip === PolicyTipType.notifyAndBlock)) {
    return {
      signal: (
        <BlockedSignal
          key={ key }
          ariaLabel={ strings.itemSignalBlocked }
          title={ strings.itemSignalBlockedTooltip }
        />
      ),
      title: strings.itemSignalBlockedTooltip,
      key: dlpBlockedSignalKey
    };
  }
  return null;
}

export function getMissingMetadataSignal(itemCache: IItemCache, itemKey: string, key: string): ISignalData | null {
  const hasMissingMetadata = itemCache.demandItemFacet(missingMetadataFacet, itemKey);
  return hasMissingMetadata ? {
    signal: (
      <MissingMetadataSignal
        key={ key }
        ariaLabel={ strings.itemSignalMissingMetadata }
        title={ strings.itemSignalMissingMetadataTooltip }
      />
    ),
    title: strings.itemSignalMissingMetadataTooltip,
    key: missingMetadataSignalKey
  } : null;
}

export function getDlpWarningSignal(itemCache: IItemCache, itemKey: string, key: string): ISignalData | null {
  const policyTip = itemCache.demandItemFacet(policyTipFacet, itemKey);
  if (policyTip && policyTip === PolicyTipType.notify) {
    return {
      signal: (
        <WarningSignal
          key={ key }
          ariaLabel={ strings.itemSignalWarning }
          title={ strings.itemSignalWarningTooltip }
        />
      ),
      title: strings.itemSignalWarningTooltip,
      key: dlpWarningSignalKey
    };
  }
  return null;
}

export function getRetentionRecordSignal(itemCache: IItemCache, itemKey: string, key: string): ISignalData | null {
  const showRecordIcon = itemCache.demandItemFacet(showRetentionPolicyIconFacet, itemKey);
  return (showRecordIcon) && {
    signal: (
      <RecordSignal
        key={ key }
        ariaLabel={ strings.itemSignalIsRecord }
        title={ strings.itemSignalIsRecordTooltip }
      />
    ),
    title: strings.itemSignalIsRecordTooltip,
    key: retentionRecordSignalKey
  } || null;
}

export function getAwaitingApprovalSignal(itemCache: IItemCache, itemKey: string, key: string): ISignalData | null {
  // to be implemented
  return null;
}

export function getTrendingSignal(itemCache: IItemCache, itemKey: string, key: string): ISignalData | null {
  const isTrendingEnabled = itemCache.demandItemFacet(enableTrendingConfigurationFacet, configurationItemKey);
  if (!isTrendingEnabled) {
    return null;
  }
  const analyticsKey = itemCache.demandItemFacet(analyticsItemFacet, itemKey);
  const itemAnalyticsKey = analyticsKey && analyticsKey.itemKey;
  const isTrending = itemAnalyticsKey && itemCache.demandItemFacet(isTrendingFacet, itemAnalyticsKey);

  return isTrending ? {
    signal: (
      <TrendingSignal
        key = { key }
        ariaLabel= { strings.itemSignalTrending }
        title= { strings.itemSignalIsTrendingTooltip }
      />
    ),
    title: strings.itemSignalIsTrendingTooltip,
    key: trendingSignalKey
  } : null;
}

export function getFollowedSignal(itemCache: IItemCache, itemKey: string, key: string): ISignalData | null {
  const followedItem = itemCache.demandItemFacet(followedItemFacet, itemKey);

  const isFollowed = itemCache.demandItemFacet(isFollowedFacet, followedItem && followedItem.itemKey);

  return isFollowed ? {
    signal: (
      <FollowedSignal
        key={ key }
        ariaLabel={ strings.itemSignalIsFollowed }
        title={ strings.itemSignalIsFollowed }
      />
    ),
    key: followedSignalKey
  } : null;
}
